import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { post } from 'api';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import * as yup from 'yup';

const folloupSchema = yup.object({
  description: yup.string().required('description is required'),
  date: yup.date().required('date is required'),
  done: yup.boolean().default(false),
  remark: yup.string().when('done', {
    is: true,
    then: (s) => s.required('remark is required'),
    otherwise: (s) => s.notRequired().nullable(), // optional when done=false
  }),
});

export default function FollowupEdit({ open, setOpen, enquiry, followUp = null, fetchData }) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(folloupSchema),
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues: {
      date: new Date(),
      description: '',
      done: false,
      remark: '',
    },
  });

  useEffect(() => {
    if (followUp) {
      const normalized = {
        description: followUp.description ?? '',
        remark: followUp.remark ?? '',
        // pick ONE source for done:
        done: !!(followUp.remark && followUp.remark.trim().length), // OR: !!followUp.status
        date: followUp.date ? moment(followUp.date).format('YYYY-MM-DD') : '',
      };

      reset(normalized); // ✅ sets all fields in one go
    }
  }, [followUp, reset]);
  const onClose = () => {
    reset();
    setOpen(false);
    fetchData();
  };

  const submitStock = async (data) => {
    try {
      if (!followUp) {
        throw Error('Error');
      }
      const res = await post(`users/update_followup/${followUp._id}`, {
        ...data,
        enquiryId: enquiry._id,
        status: Done,
      });

      if (res.status == 200) {
        enqueueSnackbar(res.message || 'Followup Updated Successfully', {
          variant: 'success',
          autoHideDuration: 3000,
        });
      } else {
        enqueueSnackbar(res.message || 'Error While Updateing Followup', {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error While Updateing Followup', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      onClose();
    }
  };

  const Done = watch('done');

  useEffect(() => {
    if (!Done) {
      setValue('remark', '');
    } else if (Done) {
      setValue('remark', followUp.remark);
    }
  }, [Done]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      component="form"
      onSubmit={handleSubmit(submitStock)}
    >
      {/* HEADER */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1 }}>
        <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>Create Enquiry</DialogTitle>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      {/* BODY */}
      <DialogContent sx={{ px: 2 }}>
        {/* VENDOR */}
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, sm: 4 }}>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Date"
                  type="date"
                  variant="filled"
                  fullWidth
                  disabled={true}
                  // InputLabelProps={{ shrink: true }}
                  error={!!errors.date}
                  helperText={errors.date?.message}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                    htmlInput: {
                      min: moment().format('YYYY-MM-DD'), // 👉 disables future dates in calendar
                      // onInvalid: (e) => e.preventDefault(), // ❗ blocks browser tooltip
                    },
                  }}
                />
              )}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 8 }}>
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  rows={3}
                  fullWidth
                  label="Description"
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                />
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 4 }}>
            <Controller
              name="done"
              control={control}
              render={({ field }) => {
                return (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                        }}
                      />
                    }
                    label="Done"
                  />
                );
              }}
            />
            {!!errors.done && <FormHelperText error>{errors.done.message}</FormHelperText>}
          </Grid>

          <Grid item size={{ xs: 12, sm: 8 }}>
            <Controller
              control={control}
              name="remark"
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  rows={3}
                  fullWidth
                  disabled={!Done}
                  label="Remark"
                  error={!!errors.remark}
                  helperText={errors.remark?.message}
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>

      {/* FOOTER */}
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" type="submit">
          Creat
        </Button>
      </DialogActions>
    </Dialog>
  );
}
